'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  type DropdownMenuProps as RadixDropdownMenuProps,
  type DropdownMenuTriggerProps as RadixDropdownMenuTriggerProps,
  type DropdownMenuContentProps as RadixDropdownMenuContentProps,
  type DropdownMenuItemProps as RadixDropdownMenuItemProps,
  type DropdownMenuSubProps as RadixDropdownMenuSubProps,
} from '@radix-ui/react-dropdown-menu';
import { cva } from 'class-variance-authority';
import { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

import { cn } from '@/lib/utils/tailwind';

export type DropdownProps = RadixDropdownMenuProps;
export type DropdownTriggerProps<T extends ElementType = 'button'> = Omit<
  RadixDropdownMenuTriggerProps,
  'asChild' | 'color'
> &
  ComponentPropsWithoutRef<T> & {
    as?: T;
    icon?: ReactNode;
  };
export type DropdownContentProps = RadixDropdownMenuContentProps;
export interface DropdownContentPropsWithTitle extends DropdownContentProps {
  title?: string;
}
export interface DropdownItemProps extends Omit<
  RadixDropdownMenuItemProps,
  'prefix' | 'color'
> {
  prefix?: ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'sm' | 'md' | 'lg';
  isActive?: boolean;
}
export interface DropdownNestedMenuProps extends RadixDropdownMenuSubProps {
  label: string;
}

const itemVariants = cva(
  'flex my-1 gap-2 rounded-(--drop-down-item-radius) cursor-pointer transition-all duration-300 ease-in-out transform border-none',
  {
    variants: {
      variant: {
        primary:
          'bg-(--drop-down-item-background-color-default) text-(--drop-down-item-text-color-default) hover:bg-(--drop-down-item-background-color-hover)  hover:text-(--drop-down-item-text-color-hover)  items-center rounded-(--drop-down-item-radius)',
        secondary:
          'items-center bg-(--drop-down-item-background-color-default) text-(--drop-down-item-text-color-default)  hover:text-(--drop-down-item-text-color-hover) hover:bg-(--drop-down-item-background-color-hover) focus-visible:outline-(--drop-down-item--border-color-active)',
        tertiary:
          'bg-(--drop-down-item-background-color-default) text-(--drop-down-item-text-color-default) hover:bg-(--drop-down-item-background-color-hover)  hover:text-(--drop-down-item-text-color-hover)  items-center rounded-(--drop-down-item-radius)',
      },
      size: {
        sm: 'px-(--drop-down-item-hspacing-sm) py-(--drop-down-item-vspacing-sm) text-(length:--text-button-md)',
        md: 'px-(--drop-down-item-hspacing-sm) py-(--drop-down-item-vspacing-sm) text-(length:--text-button-md)',
        lg: 'px-(--drop-down-item-hspacing-sm) py-(--drop-down-item-vspacing-md) text-(length:--text-button-md)',
      },
    },
    defaultVariants: {
      variant: 'tertiary',
      size: 'sm',
    },
  },
);

const DropdownRoot = ({ children, ...props }: DropdownProps) => (
  <DropdownMenu {...props}>{children}</DropdownMenu>
);

const Trigger = <T extends ElementType = 'button'>({
  as,
  children,
  icon,
  ...props
}: DropdownTriggerProps<T>) => {
  const Component = as || 'button';
  return (
    <DropdownMenuTrigger asChild>
      <Component
        role="button"
        aria-haspopup="menu"
        aria-expanded={props['data-state'] === 'open'}
        {...props}
      >
        {children}
        {icon && icon}
      </Component>
    </DropdownMenuTrigger>
  );
};

const Content = ({
  title,
  children,
  className,
  ...props
}: DropdownContentPropsWithTitle) => (
  <DropdownMenuPortal>
    <DropdownMenuContent
      align={'start'}
      avoidCollisions={true}
      collisionPadding={60}
      className={cn(
        'min-w-[180px] z-50 p-2 bg-(--drop-down-background-default) shadow-md rounded-(--drop-down-radius)',
        className,
      )}
      {...props}
    >
      {title && (
        <div className="py-2 mb-1 w-full text-center text-(--drop-down-heading-text-color-default)">
          {title}
        </div>
      )}
      {children}
    </DropdownMenuContent>
  </DropdownMenuPortal>
);

const Item = ({
  disabled,
  children,
  className,
  prefix,
  variant,
  size,
  isActive,
  ...props
}: DropdownItemProps) => (
  <DropdownMenuItem
    disabled={disabled}
    className={cn(
      itemVariants({ variant, size }),
      disabled && 'opacity-50 cursor-not-allowed',
      isActive &&
        'bg-(--drop-down-item-background-color-selected)! text-(--drop-down-item-text-color-selected)!',
      className,
    )}
    {...props}
  >
    {prefix && prefix}
    {children}
  </DropdownMenuItem>
);

const Divider = () => <DropdownMenuSeparator className="my-1 border-t" />;

const NestedMenu = ({ label, children }: DropdownNestedMenuProps) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger className="px-3 cursor-pointer">
      {label}
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent className="bg-white shadow-md p-2">
      {children}
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);

const Dropdown = Object.assign(DropdownRoot, {
  Trigger,
  Content,
  Item,
  Divider,
  Nested: NestedMenu,
}) as typeof DropdownRoot & {
  Trigger: typeof Trigger;
  Content: typeof Content;
  Item: typeof Item;
  Divider: typeof Divider;
  Nested: typeof NestedMenu;
};

export { Dropdown };
